import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Person, Timer, Search, School, FilterList, SwapHoriz } from '@material-ui/icons';
import { FixedSizeList } from 'react-window';
import request from '../../../../api';
import { Teacher } from '../../../../interfaces/examiner/Teacher';
import { Exam } from '../../../../interfaces/examiner/Exam';
import { useStyles } from './styles';
import { Selection, SendResult, SchoolGroup, ListSelectionFilter, getListSelectionFilterOptions, TeacherLocationFilter } from './types';
import {
  PAGE_SIZE,
  ROW_HEIGHT,
  addExamToSelections,
  addSchoolToSelections,
  computeTotalRecipients,
  estimateListContentWidth,
  examHasNoActiveTeachers,
  fetchAllPages,
  getActiveTeacherIds,
  getSchoolGroups,
  getSelectedExamIds,
  getSelectedSchoolIds,
  getTeacherLocations,
  invertTeacherSelectionsForTeachers,
  isExamFullySelected,
  isHtmlBodyEmpty,
  isSchoolFullySelected,
  matchesSelectionFilter,
  removeAllExamTeachersFromSelections,
  removeAllSchoolTeachersFromSelections,
} from './helpers';
import { useMeasuredHeight } from './hooks';
import { ExamRow, ExamRowData, SchoolRow, SchoolRowData, TeacherRow, TeacherRowData } from './rows';
import { ComposePanel } from './ComposePanel';
import { CopyEmailsButton } from './CopyEmailsButton';

export default function SendEmailComposer() {
  const classes = useStyles();

  const [tab, setTab] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [teachers, setTeachers] = React.useState<Teacher[]>([]);
  const [exams, setExams] = React.useState<Exam[]>([]);
  const [loadingTeachers, setLoadingTeachers] = React.useState(false);
  const [loadingExams, setLoadingExams] = React.useState(false);
  const [loadedTeachers, setLoadedTeachers] = React.useState(0);
  const [totalTeachers, setTotalTeachers] = React.useState(0);
  const [loadedExams, setLoadedExams] = React.useState(0);
  const [totalExams, setTotalExams] = React.useState(0);
  const [selections, setSelections] = React.useState<Selection[]>([]);
  const [sending, setSending] = React.useState(false);
  const [sendResult, setSendResult] = React.useState<SendResult | null>(null);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [inactiveExamSnackbarOpen, setInactiveExamSnackbarOpen] = React.useState(false);
  const [examFiltersOpen, setExamFiltersOpen] = React.useState(false);
  const [examListSelectionFilter, setExamListSelectionFilter] =
    React.useState<ListSelectionFilter>('all');
  const [schoolFiltersOpen, setSchoolFiltersOpen] = React.useState(false);
  const [schoolListSelectionFilter, setSchoolListSelectionFilter] =
    React.useState<ListSelectionFilter>('all');
  const [teacherFiltersOpen, setTeacherFiltersOpen] = React.useState(false);
  const [teacherSelectionFilter, setTeacherSelectionFilter] = React.useState<ListSelectionFilter>('all');
  const [schoolSelectionFilter, setSchoolSelectionFilter] = React.useState<ListSelectionFilter>('all');
  const [locationFilter, setLocationFilter] = React.useState<TeacherLocationFilter>('all');

  const TAB_EXAMS = 0;
  const TAB_SCHOOLS = 1;
  const TAB_TEACHERS = 2;
  const examFiltersActive = examListSelectionFilter !== 'all';
  const schoolFiltersActive = schoolListSelectionFilter !== 'all';
  const teacherFiltersActive =
    teacherSelectionFilter !== 'all' ||
    schoolSelectionFilter !== 'all' ||
    locationFilter !== 'all';

  const dataLoaded = React.useRef(false);

  React.useEffect(() => {
    if (dataLoaded.current) return;
    dataLoaded.current = true;
    let cancelled = false;
    setLoadingTeachers(true);
    setLoadingExams(true);

    (async () => {
      const [loadedTeacherList, loadedExamList] = await Promise.all([
        fetchAllPages<Teacher>(
          (offset) =>
            `/api/examiner/teachers?offset=${offset}&limit=${PAGE_SIZE}&sortBy=name&order=asc&searchQuery=&statusFilter=ACTIVE`,
          (data) => (Array.isArray(data) ? data : (data.teachers ?? [])),
          (data) => (Array.isArray(data) ? data.length : (data.total ?? 0)),
          (loaded, total) => {
            if (cancelled) return;
            setLoadedTeachers(loaded);
            setTotalTeachers(total);
          }
        ).catch(() => [] as Teacher[]),

        fetchAllPages<Exam>(
          (offset) => `/api/examiner/exams?offset=${offset}&limit=${PAGE_SIZE}&sortBy=title&order=asc&includeTeachers=true`,
          (data) => (Array.isArray(data) ? data : (data.exams ?? [])),
          (data) => (Array.isArray(data) ? data.length : (data.total ?? 0)),
          (loaded, total) => {
            if (cancelled) return;
            setLoadedExams(loaded);
            setTotalExams(total);
          }
        ).catch(() => [] as Exam[]),
      ]);

      if (cancelled) return;
      setTeachers(loadedTeacherList);
      setExams(loadedExamList);
      setLoadingTeachers(false);
      setLoadingExams(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const removeSelection = React.useCallback((sel: Selection) => {
    setSelections((prev) => prev.filter((s) => !(s.type === sel.type && s.id === sel.id)));
  }, []);

  const clearSelections = React.useCallback(() => {
    setSelections([]);
  }, []);

  const handleSend = React.useCallback(
    async (subject: string, body: string): Promise<boolean> => {
      if (!selections.some((s) => s.type === 'teacher') || !subject.trim() || isHtmlBodyEmpty(body)) {
        return false;
      }
      setSending(true);
      setSendResult(null);
      setSendError(null);
      try {
        const teacherSelections = selections
          .filter((s) => s.type === 'teacher')
          .map((s) => ({ type: 'teacher' as const, id: s.id, label: s.label }));
        const res = await request('/api/examiner/send-bulk-email', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ selections: teacherSelections, subject, body }),
        });
        if (!res.ok) {
          const text = await res.text();
          setSendError(text || 'Failed to send emails');
          return false;
        }
        const result: SendResult = await res.json();
        if (result.failed === 0) {
          setSendResult(result);
          setSelections([]);
          return true;
        }
        setSendResult(result);
        return false;
      } catch (e) {
        setSendError('Network error. Please try again.');
        return false;
      } finally {
        setSending(false);
      }
    },
    [selections]
  );

  // Failed-recipients dialog + copy state
  const [failedDialogOpen, setFailedDialogOpen] = React.useState(false);
  const [copyFailedAnimating, setCopyFailedAnimating] = React.useState(false);
  const [failedEmailsCopiedOpen, setFailedEmailsCopiedOpen] = React.useState(false);
  const copyFailedAnimTimeoutRef = React.useRef<number | null>(null);
  const failedRecipients = sendResult?.failedRecipients ?? [];

  React.useEffect(() => {
    if (sendResult && sendResult.failed > 0 && (sendResult.failedRecipients?.length ?? 0) > 0) {
      setFailedDialogOpen(true);
    }
  }, [sendResult]);

  React.useEffect(() => {
    return () => {
      if (copyFailedAnimTimeoutRef.current !== null) {
        window.clearTimeout(copyFailedAnimTimeoutRef.current);
      }
    };
  }, []);

  const handleDismissSendResult = () => {
    setFailedDialogOpen(false);
    setSendResult(null);
  };

  const handleCopyFailedEmails = async () => {
    const emails = failedRecipients.map((r) => r.email.trim()).filter((email) => email);
    if (!emails.length) return;
    try {
      await navigator.clipboard.writeText(emails.join('\n'));
      setCopyFailedAnimating(true);
      setFailedEmailsCopiedOpen(true);
      if (copyFailedAnimTimeoutRef.current !== null) {
        window.clearTimeout(copyFailedAnimTimeoutRef.current);
      }
      copyFailedAnimTimeoutRef.current = window.setTimeout(() => {
        setCopyFailedAnimating(false);
        copyFailedAnimTimeoutRef.current = null;
      }, 650);
    } catch {
      // clipboard unavailable
    }
  };

  const q = searchQuery.toLowerCase();

  const schoolGroups = React.useMemo(() => getSchoolGroups(teachers), [teachers]);
  const teacherLocations = React.useMemo(() => getTeacherLocations(teachers), [teachers]);
  const activeTeacherIds = React.useMemo(() => getActiveTeacherIds(teachers), [teachers]);

  const teacherById = React.useMemo(() => {
    const map = new Map<string, Teacher>();
    teachers.forEach((t) => map.set(t.id, t));
    exams.forEach((exam) =>
      exam.teachers?.forEach((eht) => {
        const id = eht.teacherId ?? eht.teacher?.id;
        const teacher = eht.teacher ?? (id ? map.get(id) : undefined);
        if (id && teacher) map.set(id, teacher);
      })
    );
    return map;
  }, [teachers, exams]);

  const selectedExamIds = React.useMemo(
    () => getSelectedExamIds(exams, selections, activeTeacherIds),
    [exams, selections, activeTeacherIds]
  );

  const filteredExams = React.useMemo(() => {
    let result = exams.filter((exam) => {
      if (exam.title?.toLowerCase().includes(q)) return true;
      return (
        exam.teachers?.some((eht) => {
          const id = eht.teacherId ?? eht.teacher?.id;
          const teacher = eht.teacher ?? (id ? teacherById.get(id) : undefined);
          return teacher?.school?.toLowerCase().includes(q);
        }) ?? false
      );
    });
    result = result.filter((exam) =>
      matchesSelectionFilter(selectedExamIds.has(exam.id), examListSelectionFilter)
    );
    return result;
  }, [exams, q, teacherById, selectedExamIds, examListSelectionFilter]);

  const selectedSchoolIds = React.useMemo(
    () => getSelectedSchoolIds(schoolGroups, selections),
    [schoolGroups, selections]
  );

  const filteredSchools = React.useMemo(() => {
    let schools = schoolGroups.filter((school) => school.name.toLowerCase().includes(q));
    schools = schools.filter((school) =>
      matchesSelectionFilter(selectedSchoolIds.has(school.name), schoolListSelectionFilter)
    );
    return schools;
  }, [schoolGroups, q, selectedSchoolIds, schoolListSelectionFilter]);

  const selectedTeacherIds = React.useMemo(
    () => new Set(selections.filter((s) => s.type === 'teacher').map((s) => s.id)),
    [selections]
  );

  const filteredTeachers = React.useMemo(() => {
    let result = teachers.filter(
      (t) =>
        t.name?.toLowerCase().includes(q) ||
        t.school?.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q)
    );

    if (tab === TAB_TEACHERS) {
      result = result.filter((teacher) =>
        matchesSelectionFilter(selectedTeacherIds.has(teacher.id), teacherSelectionFilter)
      );
      result = result.filter((teacher) => {
        const schoolName = teacher.school?.trim() || 'Unknown';
        return matchesSelectionFilter(selectedSchoolIds.has(schoolName), schoolSelectionFilter);
      });
      if (locationFilter !== 'all') {
        result = result.filter((teacher) => teacher.location === locationFilter);
      }
    }

    return result;
  }, [
    teachers,
    q,
    tab,
    selectedTeacherIds,
    selectedSchoolIds,
    teacherSelectionFilter,
    schoolSelectionFilter,
    locationFilter,
  ]);

  const totalRecipients = React.useMemo(
    () => computeTotalRecipients(selections, exams, schoolGroups, teacherById),
    [selections, exams, schoolGroups, teacherById]
  );

  const handleToggleExam = React.useCallback(
    (exam: Exam) => {
      if (isExamFullySelected(exam, selections, activeTeacherIds)) {
        setSelections((prev) => removeAllExamTeachersFromSelections(prev, exam, activeTeacherIds));
        return;
      }

      if (examHasNoActiveTeachers(exam, activeTeacherIds)) {
        setInactiveExamSnackbarOpen(true);
        return;
      }

      setSelections((prev) => addExamToSelections(prev, exam, teacherById, activeTeacherIds));
    },
    [selections, activeTeacherIds, teacherById]
  );
  const handleToggleSchool = React.useCallback(
    (school: SchoolGroup) => {
      if (isSchoolFullySelected(school, selections)) {
        setSelections((prev) => removeAllSchoolTeachersFromSelections(prev, school));
        return;
      }
      setSelections((prev) => addSchoolToSelections(prev, school, teacherById));
    },
    [selections, teacherById]
  );
  const handleToggleTeacher = React.useCallback((teacher: Teacher) => {
    setSelections((prev) => {
      const idx = prev.findIndex((s) => s.type === 'teacher' && s.id === teacher.id);
      if (idx >= 0) {
        return prev.filter((s) => !(s.type === 'teacher' && s.id === teacher.id));
      }
      return [
        ...prev,
        { type: 'teacher', id: teacher.id, label: teacher.name, manual: true },
      ];
    });
  }, []);

  const handleInvertFilteredTeacherSelections = React.useCallback(() => {
    setSelections((prev) => invertTeacherSelectionsForTeachers(prev, filteredTeachers));
    setTeacherSelectionFilter((current) => {
      if (current === 'selected') return 'unselected';
      if (current === 'unselected') return 'selected';
      return current;
    });
  }, [filteredTeachers]);

  const examItemData = React.useMemo<ExamRowData>(
    () => ({
      items: filteredExams,
      selectedIds: selectedExamIds,
      classes,
      onToggle: handleToggleExam,
    }),
    [filteredExams, selectedExamIds, classes, handleToggleExam]
  );
  const schoolItemData = React.useMemo<SchoolRowData>(
    () => ({
      items: filteredSchools,
      selectedIds: selectedSchoolIds,
      classes,
      onToggle: handleToggleSchool,
    }),
    [filteredSchools, selectedSchoolIds, classes, handleToggleSchool]
  );
  const teacherItemData = React.useMemo<TeacherRowData>(
    () => ({
      items: filteredTeachers,
      selectedIds: selectedTeacherIds,
      classes,
      onToggle: handleToggleTeacher,
    }),
    [filteredTeachers, selectedTeacherIds, classes, handleToggleTeacher]
  );

  const { ref: listContainerRef, height: listHeight, width: listContainerWidth } = useMeasuredHeight();

  const listWidth = React.useMemo(() => {
    const labels =
      tab === 0
        ? filteredExams.map(
          (exam) =>
            `${exam.title ?? ''} ${exam.teachers?.length ?? 0} teacher${(exam.teachers?.length ?? 0) !== 1 ? 's' : ''}`
        )
        : tab === 1
          ? filteredSchools.map(
            (school) =>
              `${school.name} ${school.teacherCount} teacher${school.teacherCount !== 1 ? 's' : ''}`
          )
          : filteredTeachers.map((teacher) => `${teacher.name ?? ''} ${teacher.school ?? ''}`);
    return estimateListContentWidth(labels, listContainerWidth);
  }, [tab, filteredExams, filteredSchools, filteredTeachers, listContainerWidth]);

  return (
    <Box className={classes.root}>
      {/* Left panel */}
      <Paper className={classes.leftPanel} variant="outlined">
        <Tabs
          className={classes.tabs}
          classes={{ flexContainer: classes.tabsFlexContainer }}
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setSearchQuery('');
            setExamFiltersOpen(false);
            setSchoolFiltersOpen(false);
            setTeacherFiltersOpen(false);
          }}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Exams" icon={<Timer fontSize="small" />} classes={{ root: classes.tabRoot }} />
          <Tab label="Schools" icon={<School fontSize="small" />} classes={{ root: classes.tabRoot }} />
          <Tab label="Teachers" icon={<Person fontSize="small" />} classes={{ root: classes.tabRoot }} />
        </Tabs>
        <Divider />
        <Box px={1} py={2}>
          <Box className={classes.searchRow}>
            <TextField
              className={classes.searchField}
              size="small"
              fullWidth
              placeholder={
                tab === TAB_EXAMS
                  ? 'Search exams or school…'
                  : tab === TAB_SCHOOLS
                    ? 'Search schools…'
                    : 'Search teachers, school or email…'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                disableUnderline: true,
                classes: { input: classes.searchInput },
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {tab === TAB_EXAMS && (
              <Tooltip title="Filter exams" arrow placement="top">
                <IconButton
                  className={`${classes.filterButton} ${
                    examFiltersOpen || examFiltersActive ? classes.filterButtonActive : ''
                  }`}
                  aria-label="Filter exams"
                  aria-expanded={examFiltersOpen}
                  onClick={() => setExamFiltersOpen((open) => !open)}
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {tab === TAB_SCHOOLS && (
              <Tooltip title="Filter schools" arrow placement="top">
                <IconButton
                  className={`${classes.filterButton} ${
                    schoolFiltersOpen || schoolFiltersActive ? classes.filterButtonActive : ''
                  }`}
                  aria-label="Filter schools"
                  aria-expanded={schoolFiltersOpen}
                  onClick={() => setSchoolFiltersOpen((open) => !open)}
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {tab === TAB_TEACHERS && (
              <Tooltip title="Filter teachers" arrow placement="top">
                <IconButton
                  className={`${classes.filterButton} ${
                    teacherFiltersOpen || teacherFiltersActive ? classes.filterButtonActive : ''
                  }`}
                  aria-label="Filter teachers"
                  aria-expanded={teacherFiltersOpen}
                  onClick={() => setTeacherFiltersOpen((open) => !open)}
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {tab === TAB_EXAMS && examFiltersOpen && (
            <Box className={classes.teacherFiltersPanel}>
              <FormControl variant="outlined" size="small" className={classes.teacherFilterControl}>
                <InputLabel id="exam-list-selection-filter-label">Exams</InputLabel>
                <Select
                  labelId="exam-list-selection-filter-label"
                  label="Exams"
                  value={examListSelectionFilter}
                  onChange={(e) => setExamListSelectionFilter(e.target.value as ListSelectionFilter)}
                >
                  {getListSelectionFilterOptions('Exams').map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {tab === TAB_SCHOOLS && schoolFiltersOpen && (
            <Box className={classes.teacherFiltersPanel}>
              <FormControl variant="outlined" size="small" className={classes.teacherFilterControl}>
                <InputLabel id="school-list-selection-filter-label">Schools</InputLabel>
                <Select
                  labelId="school-list-selection-filter-label"
                  label="Schools"
                  value={schoolListSelectionFilter}
                  onChange={(e) =>
                    setSchoolListSelectionFilter(e.target.value as ListSelectionFilter)
                  }
                >
                  {getListSelectionFilterOptions('Schools').map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          {tab === TAB_TEACHERS && teacherFiltersOpen && (
            <Box className={classes.teacherFiltersPanel}>
              <Box className={classes.teacherFilterRow}>
                <FormControl
                  variant="outlined"
                  size="small"
                  className={`${classes.teacherFilterControl} ${classes.teacherFilterRowControl}`}
                >
                  <InputLabel id="teacher-selection-filter-label">Teachers</InputLabel>
                  <Select
                    labelId="teacher-selection-filter-label"
                    label="Teachers"
                    value={teacherSelectionFilter}
                    onChange={(e) =>
                      setTeacherSelectionFilter(e.target.value as ListSelectionFilter)
                    }
                  >
                    {getListSelectionFilterOptions('Teachers').map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip
                  title="Swap selected and unselected teachers in the current list"
                  arrow
                  placement="top"
                >
                  <span>
                    <IconButton
                      className={classes.invertSelectionButton}
                      aria-label="Swap selected and unselected teachers in the current list"
                      disabled={filteredTeachers.length === 0}
                      onClick={handleInvertFilteredTeacherSelections}
                    >
                      <SwapHoriz fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <FormControl variant="outlined" size="small" className={classes.teacherFilterControl}>
                <InputLabel id="school-selection-filter-label">Schools</InputLabel>
                <Select
                  labelId="school-selection-filter-label"
                  label="Schools"
                  value={schoolSelectionFilter}
                  onChange={(e) => setSchoolSelectionFilter(e.target.value as ListSelectionFilter)}
                >
                  {getListSelectionFilterOptions('Schools').map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.teacherFilterControl}>
                <InputLabel id="location-filter-label">Location</InputLabel>
                <Select
                  labelId="location-filter-label"
                  label="Location"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value as TeacherLocationFilter)}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {teacherLocations.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        <Divider />

        <Box className={classes.listArea}>
          <Typography className={classes.sectionLabel}>
            {tab === 0
              ? `Exams (${filteredExams.length})`
              : tab === 1
                ? `Schools (${filteredSchools.length})`
                : `Individual Teachers (${filteredTeachers.length})`}
          </Typography>

          {(tab === 0 ? loadingExams : loadingTeachers) ? (
            <Box display="flex" flexDirection="column" alignItems="center" p={2} style={{ gap: 6 }}>
              <CircularProgress size={20} />
              {(tab === 0 ? totalExams : totalTeachers) > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {tab === 0
                    ? `Loading ${loadedExams} / ${totalExams} exams…`
                    : `Loading ${loadedTeachers} / ${totalTeachers} teachers…`}
                </Typography>
              )}
            </Box>
          ) : (
            <div ref={listContainerRef} className={classes.listScrollContainer}>
              {listHeight > 0 &&
                listContainerWidth > 0 &&
                (tab === 0 ? (
                  <FixedSizeList
                    height={listHeight}
                    width={listWidth}
                    itemCount={filteredExams.length}
                    itemSize={ROW_HEIGHT}
                    itemData={examItemData}
                    overscanCount={6}
                  >
                    {ExamRow}
                  </FixedSizeList>
                ) : tab === 1 ? (
                  <FixedSizeList
                    height={listHeight}
                    width={listWidth}
                    itemCount={filteredSchools.length}
                    itemSize={ROW_HEIGHT}
                    itemData={schoolItemData}
                    overscanCount={6}
                  >
                    {SchoolRow}
                  </FixedSizeList>
                ) : (
                  <FixedSizeList
                    height={listHeight}
                    width={listWidth}
                    itemCount={filteredTeachers.length}
                    itemSize={ROW_HEIGHT}
                    itemData={teacherItemData}
                    overscanCount={6}
                  >
                    {TeacherRow}
                  </FixedSizeList>
                ))}
            </div>
          )}
        </Box>
      </Paper>

      {/* Right panel */}
      <ComposePanel
        selections={selections}
        exams={exams}
        schoolGroups={schoolGroups}
        teacherById={teacherById}
        totalRecipients={totalRecipients}
        sending={sending}
        onRemoveSelection={removeSelection}
        onClearSelections={clearSelections}
        onSend={handleSend}
      />

      {/* Send result snackbar */}
      <Snackbar
        open={!!sendResult}
        autoHideDuration={sendResult?.failed ? null : 5000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          handleDismissSendResult();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={sendResult?.failed === 0 ? classes.snackbarSuccess : classes.snackbarWarning}>
          {sendResult && sendResult.failed === 0
            ? `✓ Sent successfully to ${sendResult.sent} teacher${sendResult.sent !== 1 ? 's' : ''}`
            : sendResult
              ? `⚠ Sent to ${sendResult.sent}, failed for ${sendResult.failed} teacher${sendResult.failed !== 1 ? 's' : ''}`
              : null}
        </Box>
      </Snackbar>

      <Dialog
        open={failedDialogOpen && failedRecipients.length > 0}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          setFailedDialogOpen(false);
        }}
        disableBackdropClick
        maxWidth="sm"
        fullWidth
        aria-labelledby="send-email-failed-title"
      >
        <DialogTitle id="send-email-failed-title">
          Failed to send ({failedRecipients.length})
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            The message could not be delivered to the following email addresses.
          </Typography>
          <TableContainer className={classes.confirmTable}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Teacher name</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      Email
                      <Tooltip title="Copy all failed emails">
                        <span>
                          <CopyEmailsButton
                            animating={copyFailedAnimating}
                            ariaLabel="Copy failed email addresses"
                            disabled={failedRecipients.every((r) => !r.email.trim())}
                            onClick={() => void handleCopyFailedEmails()}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedRecipients.map((row, index) => (
                  <TableRow key={`${row.email}-${index}`}>
                    <TableCell>{row.name || '-'}</TableCell>
                    <TableCell>{row.email || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDismissSendResult} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={failedEmailsCopiedOpen}
        autoHideDuration={3000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setFailedEmailsCopiedOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={classes.snackbarSuccess}>Email addresses copied to clipboard</Box>
      </Snackbar>

      {/* Error snackbar */}
      <Snackbar
        open={!!sendError}
        autoHideDuration={6000}
        onClose={() => setSendError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={classes.snackbarWarning}>{sendError}</Box>
      </Snackbar>

      <Snackbar
        open={inactiveExamSnackbarOpen}
        autoHideDuration={6000}
        onClose={(_event, reason) => {
          if (reason === 'clickaway') return;
          setInactiveExamSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box className={classes.snackbarWarning}>
          This exam cannot be selected - all assigned teachers are inactive.
        </Box>
      </Snackbar>
    </Box>
  );
}
